import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  AdviceProductDetailsBackendValues,
  ChangedByCustomer,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  ADVICE_NAMES,
  Advice,
  AdviceChanges,
  AdvicePriceCalculationResult,
  AdviceProductUpdate,
  AdviceState,
  OperationType,
} from "./interfaces";
import {
  getCorrectAdviceValues,
  mapFormToProductPriceRequest,
} from "./productPriceRequestMappers";

import { mapAdviceDataToDetailsFormData } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/utils";
import { ErrorStateOld } from "Store/interfaces/products";

const initialState: AdviceState = {
  adviceRequest: null,
  adviceResult: {
    current: null,
    isLoading: false,
    error: null,
  },
  advicePriceCalculationRequests: { initialRequest: {}, changedByCustomer: {} },
  advicePriceCalculationResults: {
    current: null,
    isLoading: false,
    error: null,
  },
};

const adviceSlice = createSlice({
  name: "advice",
  initialState,
  reducers: {
    getAdviceStart: (
      state,
      action: PayloadAction<AdviceProductDetailsBackendValues>,
    ) => {
      state.adviceRequest = action.payload;
      state.adviceResult.isLoading = true;
    },

    getAdviceSucceeded: (state, action: PayloadAction<Advice>) => {
      state.adviceResult.current = action.payload;
      state.adviceResult.isLoading = false;
      state.adviceResult.error = null;
      state.advicePriceCalculationRequests = {
        initialRequest: {},
        changedByCustomer: {},
      };
    },

    updateInitialPriceCalculationRequest: (
      state,
      action: PayloadAction<AdviceProductUpdate>,
    ) => {
      const {
        insuranceName,
        productCheckedToPurchase: checked,
        sliderValue,
        priceCalculationRequest,
      } = action.payload;
      const productCheckedToPurchase = checked !== undefined ? checked : false;
      if (!state.advicePriceCalculationRequests.initialRequest[insuranceName]) {
        state.advicePriceCalculationRequests.initialRequest[insuranceName] = {
          productCheckedToPurchase,
          sliderValue,
          priceCalculationRequest,
        };
      } else {
        state.advicePriceCalculationRequests.initialRequest[insuranceName] = {
          ...state.advicePriceCalculationRequests.initialRequest[insuranceName],
          productCheckedToPurchase,
          sliderValue,
          priceCalculationRequest,
        };
      }
    },

    updateInitialPriceCalculationRequestPrices: (
      state,
      action: PayloadAction<{
        insuranceName: ADVICE_NAMES;
        insuranceMonthlyPrice?: number;
      }>,
    ) => {
      const { insuranceName, insuranceMonthlyPrice } = action.payload;
      if (state.advicePriceCalculationRequests.initialRequest[insuranceName]) {
        state.advicePriceCalculationRequests.initialRequest[
          insuranceName
        ].insuranceMonthlyPrice = insuranceMonthlyPrice;
      }
    },

    customerChangeProductUpdate: (
      state,
      action: PayloadAction<AdviceProductUpdate>,
    ) => {
      const {
        insuranceName,
        productCheckedToPurchase: checked,
        sliderValue,
        additionalData,
        priceCalculationRequest,
      } = action.payload;
      const productCheckedToPurchase = checked !== undefined ? checked : false;
      state.advicePriceCalculationRequests.changedByCustomer[insuranceName] = {
        productCheckedToPurchase,
        sliderValue,
        priceCalculationRequest,
        additionalData,
      };
      state.advicePriceCalculationResults.isLoading = true;
    },

    updateChangedByCustomer: (
      state,
      action: PayloadAction<ChangedByCustomer>,
    ) => {
      state.advicePriceCalculationRequests.changedByCustomer =
        mapCartAdviceToAdviceChanges(action.payload);
    },

    resetChangedByCustomer: (state) => {
      state.advicePriceCalculationRequests.changedByCustomer = {};
    },

    advicePriceCalculationRequestStart: (
      state,
      _action: PayloadAction<Advice>,
    ) => {
      state.advicePriceCalculationResults.isLoading = true;
    },

    advicePriceCalculationRequestSucceeded: (
      state,
      action: PayloadAction<AdvicePriceCalculationResult[]>,
    ) => {
      if (state.advicePriceCalculationResults.current) {
        if (action.payload.length > 1) {
          state.advicePriceCalculationResults.current = action.payload;
        } else if (action.payload.length === 1) {
          state.advicePriceCalculationResults.current =
            getAdvicePriceCalculationResults(state, action.payload[0]);
        }
      } else {
        state.advicePriceCalculationResults.current = action.payload;
      }
      state.advicePriceCalculationResults.isLoading = false;
      state.advicePriceCalculationResults.error = null;
    },

    operationFailed: (
      state,
      action: PayloadAction<ErrorStateOld<OperationType>>,
    ) => {
      state.adviceResult.error = action.payload;
      state.adviceResult.isLoading = false;
      state.advicePriceCalculationResults.error = action.payload;
      state.advicePriceCalculationResults.isLoading = false;
    },
    resetAdviceState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

const getAdvicePriceCalculationResults = (
  state: AdviceState,
  payload: AdvicePriceCalculationResult,
): AdvicePriceCalculationResult[] => {
  const oldAdviceState = state.advicePriceCalculationResults
    .current as AdvicePriceCalculationResult[];
  const currentResult = payload;
  const priceCalculationIndex = oldAdviceState.findIndex(
    (result) => result.configurationName === currentResult.configurationName,
  );
  const newAdviceState = [...oldAdviceState];
  if (priceCalculationIndex !== -1) {
    newAdviceState[priceCalculationIndex] = currentResult;
    return newAdviceState;
  }
  return oldAdviceState;
};

const mapCartAdviceToAdviceChanges = (
  changedByCustomer: ChangedByCustomer,
): AdviceChanges => {
  const { insuranceAdvices, cartAdviceValues } = changedByCustomer;
  const { adviceChangedByCustomer, adviceProductValues } = cartAdviceValues;
  const adviceProductDetailsFormValues =
    mapAdviceDataToDetailsFormData(adviceProductValues);
  if (adviceChangedByCustomer && insuranceAdvices) {
    let adviceChanges = {};
    adviceChangedByCustomer.forEach((adviceChange) => {
      const insuranceAdvice =
        insuranceAdvices.advice[adviceChange.insuranceName];
      if (insuranceAdvice) {
        const priceCalculationRequest = mapFormToProductPriceRequest(
          adviceProductDetailsFormValues,
          adviceChange.insuranceName,
        );
        const newPriceCalculationRequest = {
          ...priceCalculationRequest,
          data: {
            ...priceCalculationRequest.data,
            insuredSum: adviceChange.sliderValue,
            ...(adviceChange.additionalData?.disabilityOneTimePaymentChecked
              ? {
                  coverageAmountCapital: getCorrectAdviceValues(
                    insuranceAdvice.singlePayment,
                  ).adviceValue,
                }
              : {}),
          },
        };
        adviceChanges = {
          ...adviceChanges,
          [adviceChange.insuranceName]: {
            productCheckedToPurchase: adviceChange.productCheckedToPurchase,
            sliderValue: adviceChange.sliderValue,
            priceCalculationRequest: newPriceCalculationRequest,
            ...(adviceChange.additionalData
              ? {
                  additionalData: adviceChange.additionalData,
                }
              : {}),
          },
        };
      }
    });
    return adviceChanges;
  }
  return {};
};

export const {
  advicePriceCalculationRequestStart,
  advicePriceCalculationRequestSucceeded,
  customerChangeProductUpdate,
  getAdviceStart,
  getAdviceSucceeded,
  operationFailed,
  resetChangedByCustomer,
  updateChangedByCustomer,
  updateInitialPriceCalculationRequest,
  updateInitialPriceCalculationRequestPrices,
  resetAdviceState,
} = adviceSlice.actions;
export const advice = adviceSlice.reducer;
